import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Frame159: React.FC = () => {
  const [selectedService, setSelectedService] = useState('authentication');
  const [selectedExpert, setSelectedExpert] = useState('');
  const [consultationType, setConsultationType] = useState('virtual');

  const services = [
    {
      id: 'authentication',
      name: 'Authentication & Verification',
      price: '$299',
      duration: '2-3 business days',
      description: 'Expert authentication of heritage items with detailed certification',
      features: [
        'Comprehensive authenticity analysis',
        'Material and craftsmanship verification',
        'Historical period confirmation',
        'Official authentication certificate',
        'Digital documentation package'
      ],
      icon: '🔍'
    },
    {
      id: 'appraisal',
      name: 'Professional Appraisal',
      price: '$199',
      duration: '1-2 business days',
      description: 'Detailed market valuation for insurance or resale purposes',
      features: [
        'Current market value assessment',
        'Comparable sales analysis',
        'Insurance replacement value',
        'Detailed condition report',
        'Professional appraisal document'
      ],
      icon: '💰'
    },
    {
      id: 'consultation',
      name: 'Expert Consultation',
      price: '$149',
      duration: '60 minutes',
      description: 'One-on-one consultation with heritage specialists',
      features: [
        'Personalized expert guidance',
        'Collection strategy advice',
        'Market trend insights',
        'Care and preservation tips',
        'Investment recommendations'
      ],
      icon: '👨‍🎓'
    },
    {
      id: 'restoration',
      name: 'Restoration Assessment',
      price: '$249',
      duration: '3-5 business days',
      description: 'Professional evaluation for restoration and conservation',
      features: [
        'Condition assessment report',
        'Restoration feasibility analysis',
        'Cost estimation for repairs',
        'Conservation recommendations',
        'Before/after value impact'
      ],
      icon: '🔧'
    }
  ];

  const experts = [
    {
      id: 'margaret-chen',
      name: 'Dr. Margaret Chen',
      title: 'Senior Heritage Authenticator',
      specialties: ['Victorian Timepieces', 'American Watchmaking', 'Precious Metals'],
      experience: '25+ years',
      certifications: ['IAA Certified', 'PhD Art History'],
      rating: 4.9,
      reviews: 247,
      avatar: '/api/placeholder/100/100',
      hourlyRate: '$175',
      availability: 'Available Today'
    },
    {
      id: 'james-whitmore',
      name: 'James Whitmore',
      title: 'Antique Furniture Specialist',
      specialties: ['Georgian Furniture', 'Mahogany Craftsmanship', 'Period Authenticity'],
      experience: '18+ years',
      certifications: ['CAPP Certified', 'Master Craftsman'],
      rating: 4.8,
      reviews: 189,
      avatar: '/api/placeholder/100/100',
      hourlyRate: '$150',
      availability: 'Available Tomorrow'
    },
    {
      id: 'elena-rossi',
      name: 'Elena Rossi',
      title: 'Fine Art & Jewelry Expert',
      specialties: ['Renaissance Art', 'Vintage Jewelry', 'Gemstone Analysis'],
      experience: '22+ years',
      certifications: ['GIA Graduate', 'Fine Arts PhD'],
      rating: 4.9,
      reviews: 312,
      avatar: '/api/placeholder/100/100',
      hourlyRate: '$200',
      availability: 'Available Today'
    },
    {
      id: 'robert-sterling',
      name: 'Robert Sterling',
      title: 'Silver & Metalwork Specialist',
      specialties: ['Sterling Silver', 'Hallmark Analysis', 'European Metalwork'],
      experience: '30+ years',
      certifications: ['Master Silversmith', 'Historical Society Fellow'],
      rating: 4.7,
      reviews: 156,
      avatar: '/api/placeholder/100/100',
      hourlyRate: '$165',
      availability: 'Available This Week'
    }
  ];

  const consultationTypes = [
    {
      id: 'virtual',
      name: 'Virtual Consultation',
      description: 'Video call with screen sharing and digital documentation',
      duration: '60 minutes',
      price: 'Standard Rate',
      features: ['HD video call', 'Screen sharing', 'Digital report', 'Recording available']
    },
    {
      id: 'in-person',
      name: 'In-Person Consultation',
      description: 'Expert visits your location for hands-on examination',
      duration: '90 minutes',
      price: '+$50 travel fee',
      features: ['Physical examination', 'On-site analysis', 'Detailed inspection', 'Travel included']
    },
    {
      id: 'mail-in',
      name: 'Mail-In Service',
      description: 'Ship your item to our secure facility for examination',
      duration: '3-5 business days',
      price: 'Standard Rate',
      features: ['Secure shipping', 'Insurance included', 'Detailed photos', 'Safe return']
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      service: 'Authentication',
      rating: 5,
      comment: 'Dr. Chen provided exceptional authentication service for my grandmother\'s jewelry. The detailed report gave me complete confidence in the pieces\' authenticity.',
      date: 'December 2024',
      verified: true
    },
    {
      id: 2,
      name: 'Michael Thompson',
      service: 'Appraisal',
      rating: 5,
      comment: 'Professional and thorough appraisal service. The market analysis was comprehensive and helped me make informed decisions about my collection.',
      date: 'November 2024',
      verified: true
    },
    {
      id: 3,
      name: 'Elizabeth Harper',
      service: 'Consultation',
      rating: 5,
      comment: 'Elena\'s expertise in Renaissance art was invaluable. Her insights helped me understand the historical significance of my painting.',
      date: 'November 2024',
      verified: true
    }
  ];

  const selectedServiceData = services.find(service => service.id === selectedService);
  const selectedExpertData = experts.find(expert => expert.id === selectedExpert);
  const selectedConsultationData = consultationTypes.find(type => type.id === consultationType);

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <header className="bg-white border-b border-grey-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/frame-158" className="text-blue-600 hover:text-blue-700 mr-4">
                ← Back to Search
              </Link>
              <h1 className="text-xl font-display font-semibold text-grey-900">Expert Services</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-grey-600">Professional Heritage Consultation</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-display font-bold text-grey-900 mb-4">
            Professional Heritage Expert Services
          </h2>
          <p className="text-lg text-grey-600 max-w-3xl mx-auto">
            Connect with certified heritage experts for authentication, appraisal, and consultation services. 
            Our specialists bring decades of experience to help you understand and value your collection.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Service Selection */}
          <div className="lg:col-span-2 space-y-8">
            {/* Services */}
            <div>
              <h3 className="text-xl font-display font-semibold text-grey-900 mb-6">Select Service</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {services.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className={`p-6 rounded-lg border-2 cursor-pointer transition-colors ${
                      selectedService === service.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-grey-200 bg-white hover:border-grey-300'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <span className="text-3xl">{service.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-grey-900">{service.name}</h4>
                          <span className="text-lg font-bold text-blue-600">{service.price}</span>
                        </div>
                        <p className="text-sm text-grey-600 mb-3">{service.description}</p>
                        <p className="text-xs text-grey-500 mb-3">Duration: {service.duration}</p>
                        <ul className="space-y-1">
                          {service.features.slice(0, 3).map((feature, index) => (
                            <li key={index} className="text-xs text-grey-600 flex items-center">
                              <span className="text-green-500 mr-2">•</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Consultation Type */}
            <div>
              <h3 className="text-xl font-display font-semibold text-grey-900 mb-6">Consultation Type</h3>
              <div className="space-y-4">
                {consultationTypes.map((type) => (
                  <div
                    key={type.id}
                    onClick={() => setConsultationType(type.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      consultationType === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-grey-200 bg-white hover:border-grey-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="font-medium text-grey-900">{type.name}</h4>
                          <span className="text-sm text-blue-600 font-medium">{type.price}</span>
                        </div>
                        <p className="text-sm text-grey-600 mb-3">{type.description}</p>
                        <div className="flex items-center space-x-4 text-xs text-grey-500 mb-2">
                          <span>Duration: {type.duration}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {type.features.map((feature, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-grey-100 text-grey-700">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expert Selection */}
            <div>
              <h3 className="text-xl font-display font-semibold text-grey-900 mb-6">Select Expert</h3>
              <div className="space-y-4">
                {experts.map((expert) => (
                  <div
                    key={expert.id}
                    onClick={() => setSelectedExpert(expert.id)}
                    className={`p-6 rounded-lg border-2 cursor-pointer transition-colors ${
                      selectedExpert === expert.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-grey-200 bg-white hover:border-grey-300'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <img src={expert.avatar} alt={expert.name} className="w-16 h-16 rounded-full" />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-medium text-grey-900">{expert.name}</h4>
                            <p className="text-sm text-blue-600">{expert.title}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-grey-900">{expert.hourlyRate}/hr</p>
                            <p className="text-xs text-green-600">{expert.availability}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4 mb-3">
                          <div className="flex items-center">
                            <span className="text-yellow-400 mr-1">★</span>
                            <span className="text-sm font-medium text-grey-900">{expert.rating}</span>
                            <span className="text-xs text-grey-500 ml-1">({expert.reviews} reviews)</span>
                          </div>
                          <span className="text-xs text-grey-500">{expert.experience} experience</span>
                        </div>
                        
                        <div className="mb-3">
                          <p className="text-xs text-grey-600 mb-1">Specialties:</p>
                          <div className="flex flex-wrap gap-1">
                            {expert.specialties.map((specialty, index) => (
                              <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-1">
                          {expert.certifications.map((cert, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                              {cert}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Booking Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-grey-200 p-6 sticky top-8">
              <h3 className="text-lg font-display font-semibold text-grey-900 mb-6">Booking Summary</h3>
              
              {selectedServiceData && (
                <div className="mb-6">
                  <h4 className="font-medium text-grey-900 mb-2">Selected Service</h4>
                  <div className="p-4 bg-grey-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-grey-900">{selectedServiceData.name}</span>
                      <span className="font-bold text-blue-600">{selectedServiceData.price}</span>
                    </div>
                    <p className="text-sm text-grey-600">{selectedServiceData.duration}</p>
                  </div>
                </div>
              )}

              {selectedConsultationData && (
                <div className="mb-6">
                  <h4 className="font-medium text-grey-900 mb-2">Consultation Type</h4>
                  <div className="p-4 bg-grey-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-grey-900">{selectedConsultationData.name}</span>
                      <span className="text-sm text-grey-600">{selectedConsultationData.price}</span>
                    </div>
                    <p className="text-sm text-grey-600">{selectedConsultationData.duration}</p>
                  </div>
                </div>
              )}

              {selectedExpertData && (
                <div className="mb-6">
                  <h4 className="font-medium text-grey-900 mb-2">Selected Expert</h4>
                  <div className="p-4 bg-grey-50 rounded-lg">
                    <div className="flex items-center space-x-3 mb-2">
                      <img src={selectedExpertData.avatar} alt={selectedExpertData.name} className="w-10 h-10 rounded-full" />
                      <div>
                        <p className="font-medium text-grey-900">{selectedExpertData.name}</p>
                        <p className="text-xs text-grey-600">{selectedExpertData.title}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-grey-600">{selectedExpertData.hourlyRate}/hr</span>
                      <span className="text-xs text-green-600">{selectedExpertData.availability}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t border-grey-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-medium text-grey-900">Total</span>
                  <span className="text-xl font-bold text-grey-900">
                    {selectedServiceData?.price || '$0'}
                  </span>
                </div>
                
                <button 
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-grey-300 disabled:cursor-not-allowed"
                  disabled={!selectedService || !selectedExpert}
                >
                  Book Consultation
                </button>
                
                <p className="text-xs text-grey-500 text-center mt-3">
                  Secure booking • 24/7 support • Money-back guarantee
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-16">
          <h3 className="text-2xl font-display font-bold text-grey-900 mb-8 text-center">Client Testimonials</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white rounded-lg border border-grey-200 p-6">
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400">★</span>
                  ))}
                </div>
                <p className="text-grey-700 mb-4">"{testimonial.comment}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-grey-900">{testimonial.name}</p>
                    <p className="text-sm text-grey-600">{testimonial.service} Service</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-grey-500">{testimonial.date}</p>
                    {testimonial.verified && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                        ✓ Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Frame159; 